import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertService, CustomerUsersService } from '../../../core/services';

/**
 * Sub-user self-service password change (customer-users guide §7).
 * PUT /customer-users/me/password — always allowed for a sub-user session,
 * regardless of granted modules.
 */
@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslateModule
  ],
  template: `
    <div class="change-password-dialog">
      <h2 mat-dialog-title>{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.TITLE' | translate }}</h2>
      <form [formGroup]="form" (ngSubmit)="submit()">
        <mat-dialog-content>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.CURRENT' | translate }}</mat-label>
            <input matInput type="password" formControlName="currentPassword" autocomplete="current-password">
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.NEW' | translate }}</mat-label>
            <input matInput type="password" formControlName="newPassword" autocomplete="new-password">
          </mat-form-field>

          <mat-form-field appearance="outline" class="full-width">
            <mat-label>{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.CONFIRM' | translate }}</mat-label>
            <input matInput type="password" formControlName="confirmPassword" autocomplete="new-password">
          </mat-form-field>

          @if (form.controls.confirmPassword.touched && form.hasError('mismatch')) {
            <p class="form-error">{{ 'CUSTOMER_USERS.CHANGE_PASSWORD.MISMATCH' | translate }}</p>
          }

          @if (errorKey()) {
            <p class="form-error">{{ errorKey()! | translate }}</p>
          }
        </mat-dialog-content>
        <mat-dialog-actions align="end">
          <button mat-button type="button" mat-dialog-close [disabled]="loading()">
            {{ 'COMMON.CANCEL' | translate }}
          </button>
          <button mat-raised-button color="primary" type="submit" [disabled]="loading()">
            @if (loading()) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              {{ 'CUSTOMER_USERS.CHANGE_PASSWORD.SUBMIT' | translate }}
            }
          </button>
        </mat-dialog-actions>
      </form>
    </div>
  `,
  styles: [`
    .change-password-dialog {
      padding: 8px;
    }

    .full-width {
      width: 100%;
    }

    mat-dialog-content {
      display: flex;
      flex-direction: column;
      gap: 4px;
      padding-top: 8px;
    }

    .form-error {
      color: #f44336;
      font-size: 13px;
      margin: 0;
    }

    mat-dialog-actions button mat-spinner {
      display: inline-block;
    }
  `]
})
export class ChangePasswordDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent>);
  private readonly customerUsers = inject(CustomerUsersService);
  private readonly alert = inject(AlertService);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(false);
  readonly errorKey = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPassword: ['', [Validators.required, Validators.minLength(4)]],
    confirmPassword: ['', Validators.required]
  }, {
    validators: [(group: AbstractControl): ValidationErrors | null =>
      group.get('newPassword')?.value === group.get('confirmPassword')?.value
        ? null
        : { mismatch: true }
    ]
  });

  submit(): void {
    this.errorKey.set(null);
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    const { currentPassword, newPassword } = this.form.getRawValue();

    this.loading.set(true);
    this.customerUsers.changeOwnPassword(currentPassword, newPassword).subscribe({
      next: () => {
        this.loading.set(false);
        this.alert.showSuccess(this.translate.instant('CUSTOMER_USERS.CHANGE_PASSWORD.SUCCESS'));
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.loading.set(false);
        const code = typeof err?.error?.error === 'string' ? err.error.error : '';
        this.errorKey.set(
          code === 'INVALID_PASSWORD' ? 'CUSTOMER_USERS.CHANGE_PASSWORD.INVALID_PASSWORD'
          : code === 'PASSWORD_UNCHANGED' ? 'CUSTOMER_USERS.CHANGE_PASSWORD.PASSWORD_UNCHANGED'
          : 'COMMON.ERROR'
        );
      }
    });
  }
}
